<div class="main-content">
	<div class="container-fluid">
		<mat-card class="feature-surface">
			<mat-card-header>
				<div mat-card-avatar class="feature-surface__icon">
					<i class="material-icons" aria-hidden="true">autorenew</i>
				</div>
				<mat-card-title>Recurring Replenishment</mat-card-title>
				<mat-card-subtitle>Customers who reorder on a cycle</mat-card-subtitle>
			</mat-card-header>
			<mat-card-content>

				<div *ngIf="snapshot" class="d-flex justify-content-between align-items-center mb-2">
					<span class="text-muted" style="font-size: 12px;">
						Last 12 months · updated {{ snapshot.computed_at | date:'MMM d, h:mm a' }}
					</span>
					<app-status-pill *ngIf="snapshot.stale" status="failed" label="out of date"></app-status-pill>
				</div>

				<form [formGroup]="filterForm" class="row mb-2">
					<div class="col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Show</mat-label>
							<mat-select formControlName="grade" (selectionChange)="load()">
								<mat-option value="A">Only the most predictable</mat-option>
								<mat-option value="AB">Predictable buyers</mat-option>
								<mat-option value="ABC">Include occasional buyers</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-2" *ngIf="view === 'due'">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Due within (days)</mat-label>
							<input matInput type="number" min="0" max="120" formControlName="horizon">
						</mat-form-field>
					</div>
					<div class="col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Salesman</mat-label>
							<mat-select formControlName="salesman" (selectionChange)="load()">
								<mat-option value="">All</mat-option>
								<mat-option *ngFor="let s of salesmen" [value]="s.code">{{ s.name }}</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Branch</mat-label>
							<mat-select formControlName="branch" (selectionChange)="load()">
								<mat-option value="">All</mat-option>
								<mat-option *ngFor="let b of branches" [value]="b.code">{{ b.code }} · {{ b.name }}</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-2">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>How often</mat-label>
							<mat-select formControlName="cadence" (selectionChange)="load()">
								<mat-option value="">Any</mat-option>
								<mat-option *ngFor="let c of cadences" [value]="c">{{ cadenceLabel(c) }}</mat-option>
							</mat-select>
						</mat-form-field>
					</div>
					<div class="col-md-2 d-flex align-items-start">
						<mat-form-field appearance="outline" class="w-100">
							<mat-label>Search</mat-label>
							<input matInput formControlName="search" (keyup.enter)="load()" placeholder="customer or item">
						</mat-form-field>
					</div>
				</form>

				<div class="d-flex mb-3 flex-wrap align-items-center" style="gap: 8px;">
					<button mat-flat-button [color]="view === 'due' ? 'accent' : ''" (click)="setView('due')">Due Now</button>
					<button mat-flat-button [color]="view === 'customers' ? 'accent' : ''" (click)="setView('customers')">Customers</button>
					<button mat-flat-button [color]="view === 'items' ? 'accent' : ''" (click)="setView('items')">Items</button>

					<span class="text-muted ml-3" style="font-size: 12px;">Group by</span>
					<button mat-stroked-button [color]="level === 'line' ? 'accent' : ''" (click)="setLevel('line')"
						matTooltip="One row per product line — a salon that rotates shades still reads as one regular buyer">Product line</button>
					<button mat-stroked-button [color]="level === 'sku' ? 'accent' : ''" (click)="setLevel('sku')"
						matTooltip="One row per individual item">Single item</button>

					<span class="flex-grow-1"></span>
					<button mat-stroked-button color="accent" (click)="load()">Apply</button>
					<button mat-stroked-button color="accent" (click)="exportCsv()" [disabled]="!rows.length">
						<i class="material-icons" style="vertical-align: -4px;" aria-hidden="true">file_download</i> Export CSV
					</button>
				</div>

				<div *ngIf="errorMessage" class="feature-section mb-3">
					<p class="feature-section__title m-0">Nothing to show</p>
					<span class="feature-section__hint">{{ errorMessage }}</span>
				</div>

				<div class="feature-tile-row" *ngIf="!loading && view === 'due' && totals">
					<app-kpi-tile
						tone="accent"
						label="lines due"
						[value]="totals.due_lines | number"
						[sub]="(totals.due_customers | number) + ' customers'">
					</app-kpi-tile>
					<app-kpi-tile
						tone="partial"
						label="overdue"
						[value]="totals.overdue_lines | number">
					</app-kpi-tile>
					<app-kpi-tile
						tone="settled"
						label="est. value"
						[value]="'$' + (totals.est_value | number:'1.0-0')">
					</app-kpi-tile>
				</div>

				<div class="feature-tile-row" *ngIf="!loading && view === 'customers' && totals">
					<app-kpi-tile tone="accent" label="customers" [value]="totals.customers | number"></app-kpi-tile>
					<app-kpi-tile tone="settled" label="their yearly spend" [value]="'$' + (totals.recurring_rev | number:'1.0-0')"></app-kpi-tile>
					<app-kpi-tile tone="partial" label="items overdue" [value]="totals.items_overdue | number"></app-kpi-tile>
				</div>

				<div *ngIf="loading" class="text-center my-4">
					<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
				</div>

				<!-- DUE NOW -->
				<table class="feature-table feature-table--responsive" *ngIf="!loading && view === 'due'">
					<thead>
						<tr>
							<th><button class="sort-head" (click)="sortBy('customer')">Customer <i class="material-icons" aria-hidden="true">{{ sortIcon('customer') }}</i></button></th>
							<th><button class="sort-head" (click)="sortBy('rep')">Rep <i class="material-icons" aria-hidden="true">{{ sortIcon('rep') }}</i></button></th>
							<th><button class="sort-head" (click)="sortBy('item')">Item <i class="material-icons" aria-hidden="true">{{ sortIcon('item') }}</i></button></th>
							<th><button class="sort-head" (click)="sortBy('late')">Why it's due <i class="material-icons" aria-hidden="true">{{ sortIcon('late') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('suggest')">Suggest <i class="material-icons" aria-hidden="true">{{ sortIcon('suggest') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('value')">Value <i class="material-icons" aria-hidden="true">{{ sortIcon('value') }}</i></button></th>
							<th></th>
						</tr>
					</thead>
					<tbody>
						<ng-container *ngFor="let r of rows">
						<tr>
							<td data-label="Customer">
								<span>
									<button mat-button class="p-0" (click)="openCustomer(r)" [attr.aria-label]="'Show what ' + r.customer + ' reorders'">
										<strong>{{ r.customer }}</strong>
									</button>
									<div class="text-muted" style="font-size: 11px;">{{ r.debtorno }} · {{ r.branch }} · {{ r.channel }}</div>
								</span>
							</td>
							<td data-label="Rep">{{ r.salesman_name }}</td>
							<td data-label="Item">
								<span>
									<strong>{{ r.item }}</strong>
									<div class="text-muted" style="font-size: 11px;">
										{{ r.code }}
										<button *ngIf="level === 'line' && r.sku_count > 1" mat-button class="p-0 ml-1"
											(click)="toggleSkus(r)"
											[attr.aria-label]="'Show the ' + r.sku_count + ' items behind ' + r.item">
											{{ isExpanded(r) ? 'hide' : r.sku_count + ' items' }}
										</button>
									</div>
								</span>
							</td>
							<td data-label="Why it's due">
								<span>
									{{ buyingPattern(r) }}
									<div class="text-muted" style="font-size: 11px;">{{ whyDue(r) }}</div>
								</span>
							</td>
							<td data-label="Suggest" class="is-num">{{ r.suggested_qty | number:'1.0-2' }}</td>
							<td data-label="Value" class="is-num">{{ r.est_line_value | currency }}</td>
							<td>
								<button mat-icon-button (click)="openHistory(r.debtorno, r.code)"
									matTooltip="Show every order we counted"
									[attr.aria-label]="'Show every order we counted for ' + r.item">
									<i class="material-icons" aria-hidden="true">history</i>
								</button>
							</td>
						</tr>
						<tr *ngIf="isExpanded(r)">
							<td colspan="7">
								<div *ngIf="skusLoading" class="text-center my-2">
									<mat-progress-spinner diameter="24" mode="indeterminate" color="accent"></mat-progress-spinner>
								</div>
								<table class="feature-table feature-table--responsive" *ngIf="!skusLoading">
									<thead>
										<tr>
											<th>Item</th>
											<th class="is-num">Times<br>bought</th>
											<th class="is-num">Every</th>
											<th>Last bought</th>
											<th>Next due</th>
											<th class="is-num">Suggest</th>
											<th class="is-num">Spend<br>(12 mo)</th>
											<th></th>
										</tr>
									</thead>
									<tbody>
										<tr *ngFor="let s of skuRows">
											<td data-label="Item">
												<span>
													{{ s.code }}
													<div class="text-muted" style="font-size: 11px;">{{ s.item }}</div>
												</span>
											</td>
											<td data-label="Times bought" class="is-num">{{ s.buys }}</td>
											<td data-label="Every" class="is-num">{{ s.cycle_days }} days</td>
											<td data-label="Last bought">{{ s.last_buy | date:'MMM d, y' }}</td>
											<td data-label="Next due">{{ s.next_due | date:'MMM d, y' }}</td>
											<td data-label="Suggest" class="is-num">{{ s.suggested_qty | number:'1.0-2' }}</td>
											<td data-label="Spend (12 mo)" class="is-num">{{ s.revenue_12mo | currency }}</td>
											<td>
												<button mat-icon-button (click)="openSkuHistory(r.debtorno, s.code)"
													matTooltip="Show every order for this item"
													[attr.aria-label]="'Show every order for ' + s.code">
													<i class="material-icons" aria-hidden="true">history</i>
												</button>
											</td>
										</tr>
										<tr *ngIf="!skuRows.length">
											<td colspan="8" class="text-center text-muted">No items found</td>
										</tr>
									</tbody>
								</table>
							</td>
						</tr>
						</ng-container>
						<tr *ngIf="!rows.length && !errorMessage">
							<td colspan="7" class="text-center text-muted">Nobody is due in this window 🎉</td>
						</tr>
					</tbody>
				</table>

				<!-- CUSTOMERS -->
				<table class="feature-table feature-table--responsive" *ngIf="!loading && view === 'customers'">
					<thead>
						<tr>
							<th><button class="sort-head" (click)="sortBy('customer')">Customer <i class="material-icons" aria-hidden="true">{{ sortIcon('customer') }}</i></button></th>
							<th><button class="sort-head" (click)="sortBy('rep')">Rep <i class="material-icons" aria-hidden="true">{{ sortIcon('rep') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('items')">Items they reorder <i class="material-icons" aria-hidden="true">{{ sortIcon('items') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('cycle')">Typically every <i class="material-icons" aria-hidden="true">{{ sortIcon('cycle') }}</i></button></th>
							<th><button class="sort-head" (click)="sortBy('last')">Last bought <i class="material-icons" aria-hidden="true">{{ sortIcon('last') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('due')">Due now <i class="material-icons" aria-hidden="true">{{ sortIcon('due') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('stopped')">Stopped buying <i class="material-icons" aria-hidden="true">{{ sortIcon('stopped') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('value')">Spend (12 mo) <i class="material-icons" aria-hidden="true">{{ sortIcon('value') }}</i></button></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let r of rows">
							<td data-label="Customer">
								<span>
									<button mat-button class="p-0" (click)="openCustomer(r)" [attr.aria-label]="'Show recurring basket for ' + r.customer">
										<strong>{{ r.customer }}</strong>
									</button>
									<div class="text-muted" style="font-size: 11px;">{{ r.debtorno }}</div>
								</span>
							</td>
							<td data-label="Rep">{{ r.salesman_name }}</td>
							<td data-label="Items they reorder" class="is-num">{{ r.recurring_items }}</td>
							<td data-label="Typically every" class="is-num">{{ r.avg_cycle_days }} days</td>
							<td data-label="Last bought">{{ r.last_purchase | date:'MMM d, y' }}</td>
							<td data-label="Due now" class="is-num">{{ r.items_overdue }}</td>
							<td data-label="Stopped buying" class="is-num">{{ r.items_lapsed }}</td>
							<td data-label="Spend (12 mo)" class="is-num">{{ r.recurring_revenue_12mo | currency }}</td>
						</tr>
						<tr *ngIf="!rows.length && !errorMessage">
							<td colspan="8" class="text-center text-muted">No customers match this filter</td>
						</tr>
					</tbody>
				</table>

				<!-- ITEMS -->
				<table class="feature-table feature-table--responsive" *ngIf="!loading && view === 'items'">
					<thead>
						<tr>
							<th><button class="sort-head" (click)="sortBy('item')">Item <i class="material-icons" aria-hidden="true">{{ sortIcon('item') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('custs')">Customers who reorder it <i class="material-icons" aria-hidden="true">{{ sortIcon('custs') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('cycle')">Typically every <i class="material-icons" aria-hidden="true">{{ sortIcon('cycle') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('qty')">Qty each time <i class="material-icons" aria-hidden="true">{{ sortIcon('qty') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('due')">Due this week <i class="material-icons" aria-hidden="true">{{ sortIcon('due') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('units')">Units a year if all enrolled <i class="material-icons" aria-hidden="true">{{ sortIcon('units') }}</i></button></th>
							<th class="is-num"><button class="sort-head" (click)="sortBy('value')">Sales (12 mo) <i class="material-icons" aria-hidden="true">{{ sortIcon('value') }}</i></button></th>
						</tr>
					</thead>
					<tbody>
						<tr *ngFor="let r of rows">
							<td data-label="Item">
								<span>
									{{ r.code }}
									<div class="text-muted" style="font-size: 11px;">{{ r.item }}</div>
								</span>
							</td>
							<td data-label="Customers who reorder it" class="is-num">{{ r.subscriber_custs | number }}</td>
							<td data-label="Typically every" class="is-num">{{ r.avg_cycle_days }} days</td>
							<td data-label="Qty each time" class="is-num">{{ r.avg_qty_per_cycle | number:'1.0-2' }}</td>
							<td data-label="Due this week" class="is-num">{{ r.due_this_week | number }}</td>
							<td data-label="Units a year if all enrolled" class="is-num">{{ r.implied_annual_units | number:'1.0-0' }}</td>
							<td data-label="Sales (12 mo)" class="is-num">{{ r.revenue_12mo | currency }}</td>
						</tr>
						<tr *ngIf="!rows.length && !errorMessage">
							<td colspan="7" class="text-center text-muted">No items match this filter</td>
						</tr>
					</tbody>
				</table>

				<p *ngIf="truncated" class="text-muted mt-2" style="font-size: 12px;">
					Showing the first {{ limit | number }} rows — narrow the filters or export for the full set.
				</p>

				<!-- DRILL-IN: one customer's recurring basket -->
				<div *ngIf="selected" class="feature-section mt-4">
					<div class="d-flex justify-content-between align-items-center mb-2">
						<p class="feature-section__title m-0">What {{ selected.customer }} reorders</p>
						<span>
							<button mat-stroked-button color="accent" (click)="goToCustomer(selected.debtorno)">Open customer</button>
							<button mat-icon-button (click)="selected = null" aria-label="Close basket">
								<i class="material-icons" aria-hidden="true">close</i>
							</button>
						</span>
					</div>

					<span class="feature-section__hint">"Stopped" means they've missed more than a full cycle.</span>

					<div *ngIf="detailLoading" class="text-center my-3">
						<mat-progress-spinner diameter="28" mode="indeterminate" color="accent"></mat-progress-spinner>
					</div>

					<div class="feature-tile-row mt-3" *ngIf="!detailLoading && detailSummary">
						<app-kpi-tile tone="accent" label="items they reorder" [value]="detailSummary.items"></app-kpi-tile>
						<app-kpi-tile tone="partial" label="due now" [value]="detailSummary.due_now"></app-kpi-tile>
						<app-kpi-tile tone="failed" label="stopped buying" [value]="detailSummary.lapsed"></app-kpi-tile>
						<app-kpi-tile tone="settled" label="spend (12 mo)" [value]="'$' + (detailSummary.revenue_12mo | number:'1.0-0')"></app-kpi-tile>
					</div>

					<table class="feature-table feature-table--responsive mt-3" *ngIf="!detailLoading">
						<thead>
							<tr>
								<th>Item</th>
								<th>Buying pattern</th>
								<th class="is-num">Bought</th>
								<th>Last bought</th>
								<th class="is-num">Suggest</th>
								<th>Status</th>
								<th class="is-num">Spend<br>(12 mo)</th>
								<th></th>
							</tr>
						</thead>
						<tbody>
							<tr *ngFor="let d of detailRows">
								<td data-label="Item">
									<span>
										{{ d.code }}
										<div class="text-muted" style="font-size: 11px;">{{ d.item }}</div>
									</span>
								</td>
								<td data-label="Buying pattern">
									<span>
										{{ buyingPattern(d) }}
										<div class="text-muted" style="font-size: 11px;">between {{ d.min_gap }} and {{ d.max_gap }} days apart</div>
									</span>
								</td>
								<td data-label="Bought" class="is-num">{{ d.buys }} times</td>
								<td data-label="Last bought">{{ d.last_buy | date:'MMM d, y' }}</td>
								<td data-label="Suggest" class="is-num">{{ d.suggested_qty | number:'1.0-2' }}</td>
								<td data-label="Status">
									<span *ngIf="d.lapsed" class="text-muted">stopped buying</span>
									<span *ngIf="!d.lapsed">{{ lateLabel(d.days_late) }}</span>
								</td>
								<td data-label="Spend (12 mo)" class="is-num">{{ d.revenue_12mo | currency }}</td>
								<td>
									<button mat-icon-button (click)="openHistory(selected.debtorno, d.code)"
										matTooltip="Show the orders behind this"
										[attr.aria-label]="'Show the orders behind ' + d.code">
										<i class="material-icons" aria-hidden="true">history</i>
									</button>
								</td>
							</tr>
							<tr *ngIf="!detailRows.length">
								<td colspan="8" class="text-center text-muted">Nothing they reorder yet</td>
							</tr>
						</tbody>
					</table>
				</div>

			</mat-card-content>
		</mat-card>
	</div>
</div>

<!-- Why this cycle: the orders the number was measured from. The Gap column is
     the series the cycle averages, so it can be checked rather than trusted. -->
<ng-template #historyDialog>
	<h2 mat-dialog-title>{{ history?.item || 'Purchase history' }}</h2>

	<div mat-dialog-content>
		<div *ngIf="historyLoading" class="text-center my-4">
			<mat-progress-spinner diameter="32" mode="indeterminate" color="accent"></mat-progress-spinner>
		</div>

		<ng-container *ngIf="!historyLoading && history">
			<p class="text-muted mb-2" style="font-size: 12px;">
				{{ history.customer }} · {{ history.item }}
			</p>

			<div class="feature-tile-row">
				<app-kpi-tile tone="accent" label="buys every" [value]="history.score.avg_gap + ' days'"></app-kpi-tile>
				<app-kpi-tile tone="default" label="times bought" [value]="history.score.buys"></app-kpi-tile>
				<app-kpi-tile tone="default" label="usual qty" [value]="history.score.avg_qty"></app-kpi-tile>
				<app-kpi-tile tone="settled" label="due" [value]="history.score.next_due | date:'MMM d'"></app-kpi-tile>
			</div>

			<p class="text-muted mt-2 mb-0" style="font-size: 11px;">
				Gap = days since the order above it. That's what the average is built from.
			</p>

			<table class="feature-table feature-table--responsive mt-3">
				<thead>
					<tr>
						<th>Date</th>
						<th class="is-num">Gap</th>
						<th>Order</th>
						<th>Invoice</th>
						<th class="is-num">Qty</th>
						<th class="is-num">Price</th>
						<th class="is-num">Total</th>
						<th>Where</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let h of history.rows">
						<td data-label="Date">{{ h.orddate | date:'MMM d, y' }}</td>
						<td data-label="Gap" class="is-num">{{ h.gap_days ? h.gap_days + 'd' : '—' }}</td>
						<td data-label="Order">{{ h.orders }}</td>
						<td data-label="Invoice">{{ h.invoices || '—' }}</td>
						<td data-label="Qty" class="is-num">{{ h.qty | number:'1.0-2' }}</td>
						<td data-label="Price" class="is-num">{{ h.unitprice | currency }}</td>
						<td data-label="Total" class="is-num">{{ h.ext | currency }}</td>
						<td data-label="Where">{{ h.branch }} · {{ h.channel }}</td>
					</tr>
					<tr *ngIf="!history.rows.length">
						<td colspan="8" class="text-center text-muted">No orders in the scoring window</td>
					</tr>
				</tbody>
			</table>
		</ng-container>

		<p *ngIf="!historyLoading && !history" class="text-muted text-center my-4">
			Could not load the purchase history.
		</p>
	</div>

	<div mat-dialog-actions class="d-flex justify-content-end">
		<button mat-stroked-button mat-dialog-close>Close</button>
	</div>
</ng-template>
